import { useState } from 'react';

import { FormError } from '@common/components/form/FormError';
import { FormField } from '@common/components/form/FormField';
import { Label } from '@common/components/form/Label';
import { RadioField } from '@common/components/RadioGroupField/RadioField';
import { Spinner } from '@common/components/Spinner';
import { useRoomSettings } from '@features/roomSettings';
import { useTablesSettings } from '@features/tables/hooks/useGetTablesSettings';
import { useGetSpaSettings } from '@features/spa/hooks';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { useTranslations } from '@lang';
import { isNotNullish } from '@utils/isNotNullish';

import { useRouter } from 'next/router';
import { OrderNumber, RadioContainer } from '../Account.styles';
import { allItemsCancellable } from '../helpers/allItemsCancellable.helper';
import { useAmendOrder, useCancelOrder } from '../mutations';
import { useGetUnauthenticatedOrder } from '../queries';
import { routeStates } from '../routes/CancelOrder';
import { AccountHeader } from './AccountHeader';
import { NewCancelOrderForm } from './NewCancelOrderForm';
import { RequestOrderModificationForm } from './RequestOrderModificationForm';

type ModifyOrderFormProps = {
  siteSlug: string;
  orderUuid: string;
  siteUuid: string;
  propertyId: number;
};

export function ModifyOrderForm({
  siteSlug,
  orderUuid,
  siteUuid,
  propertyId,
}: ModifyOrderFormProps) {
  const { t } = useTranslations();
  const router = useRouter();

  const { data: order, isLoading } = useGetUnauthenticatedOrder({
    propertyId: siteUuid,
    orderId: orderUuid,
  });

  const { data: rooms } = useRoomSettings();
  const { data: tables } = useTablesSettings();
  const { data: spa } = useGetSpaSettings(propertyId);

  const cancellationsEnabled = {
    spa: spa?.isCancellationsEnabled,
    rooms: rooms?.data?.cancellationsEnabled,
    tables: tables?.data?.cancellationsEnabled,
  };
  const [isCancelling, setIsCancelling] = useState(false);

  const areAllItemsCancellable = allItemsCancellable({
    order,
    cancellationsEnabled,
  });

  const showNewCancellationForm =
    useFeatureIsOn('cancellations') && isCancelling && areAllItemsCancellable;

  const onOrderRequestSuccess = () =>
    router.replace({
      query: {
        ...router.query,
        state: showNewCancellationForm ? routeStates.CANCELLED : routeStates.SUCCESS,
      },
    });
  const {
    mutate: amendOrder,
    isLoading: isAmendLoading,
    isError: isAmendError,
    error: amendError,
  } = useAmendOrder({
    onSuccess: onOrderRequestSuccess,
  });

  const {
    mutate: cancelOrder,
    isLoading: isCancelLoading,
    isError: isCancelError,
    error: cancelError,
  } = useCancelOrder({
    onSuccess: onOrderRequestSuccess,
  });

  if (isLoading) {
    return <Spinner isActive showDelayMessage />;
  }

  const orderWithoutCancelledItems = order && {
    ...order,
    items: order.items.filter(
      (item) => item.status !== 'cancelled' && item.status !== 'cancellation_failed'
    ),
  };

  return (
    <>
      {!!order && (
        <AccountHeader isCompact>
          {isCancelling ? t('cancelOrder.heading') : t('amendOrder.heading')}
        </AccountHeader>
      )}
      <OrderNumber>
        <div>Order Number</div>
        <div>{order ? order?.order_reference : 'ORDER NOT FOUND'}</div>
      </OrderNumber>

      {isCancelling ? <p>{t('cancelOrderForm.title')}</p> : <p>{t('amendOrderForm.title')}</p>}

      {isAmendError && <FormError error={amendError} />}
      {isCancelError && <FormError error={cancelError} />}

      <FormField>
        <Label htmlFor='="requestType' required>
          {t('orderRequest.requestType')}
        </Label>
        <RadioContainer>
          <RadioField
            data-testid="amend-radio-button"
            onChange={() => setIsCancelling(false)}
            isChecked={!isCancelling}
            value={'amend'}
          >
            <span>Amendment</span>
          </RadioField>

          <RadioField
            data-testid="cancel-radio-button"
            onChange={() => setIsCancelling(true)}
            isChecked={isCancelling}
            value={'cancel'}
          >
            <span>Cancellation</span>
          </RadioField>
        </RadioContainer>
      </FormField>

      {isNotNullish(order) && !showNewCancellationForm && (
        <RequestOrderModificationForm
          siteSlug={siteSlug}
          orderUuid={orderUuid}
          isCancelling={isCancelling}
          amendOrder={amendOrder}
          cancelOrder={cancelOrder}
          isLoading={isAmendLoading || isCancelLoading}
        />
      )}
      {showNewCancellationForm && isNotNullish(orderWithoutCancelledItems) && (
        <NewCancelOrderForm
          data-testid="anon-modify-order-form"
          siteUuid={siteUuid}
          order={orderWithoutCancelledItems}
          onOrderRequestSuccess={onOrderRequestSuccess}
        />
      )}
    </>
  );
}
